import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  FaConciergeBell,
  FaEdit,
  FaPlus,
  FaTrash,
  FaRupeeSign,
  FaBriefcase,
  FaCalculator,
  FaSearch,
  FaAngleLeft,
  FaAngleRight,
  FaAngleDoubleLeft,
  FaAngleDoubleRight
} from 'react-icons/fa';
import Modal from '../components/Modal/Modal';
import CustomSelect from '../components/CustomSelect/CustomSelect';
import PageHero from '../components/PageHero/PageHero';
import { storage } from '../utils/storage';
import useConfirm from '../hooks/useConfirm';
import { STORAGE_KEYS, API_BASE_URL } from '../utils/constants';
import session from '../utils/session';
import showToast from '../utils/toast';
import './Services.scss';
import { useLanguage } from '../context/LanguageContext';

const Services = () => {
  const { t } = useLanguage();
  // start with persisted services if available
  const [services, setServices] = useState(() => {
    const persisted = storage.getJSON(STORAGE_KEYS.SERVICES, null);
    return Array.isArray(persisted) ? persisted : [];
  });

  const [showAddModal, setShowAddModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [filterCategory, setFilterCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [priceSort, setPriceSort] = useState('default');
  const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);
  const [newCategoryInput, setNewCategoryInput] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    description: '',
    basePrice: '',
    deliverables: [],
    features: []
  });
  const [formErrors, setFormErrors] = useState({});
  const confirm = useConfirm();

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(9);

  // Get unique categories dynamically from services
  const getUniqueCategories = () => {
    const cats = new Set(services.map(s => s.category).filter(Boolean));
    // include current form value so newly-entered category shows in the select
    if (formData && formData.category) {
      const c = String(formData.category).trim();
      if (c) cats.add(c);
    }
    return ['all', ...Array.from(cats).sort()];
  };

  const handleAddService = () => {
    setEditingService(null);
    setShowNewCategoryInput(false);
    setNewCategoryInput('');
    setFormErrors({});
    setFormData({
      name: '',
      category: '',
      description: '',
      basePrice: '',
      deliverables: [],
      features: []
    });
    setShowAddModal(true);
  };

  const handleEditService = (service) => {
    setEditingService(service);
    setShowNewCategoryInput(false);
    setNewCategoryInput('');
    setFormErrors({});
    setFormData({
      name: service.name,
      category: service.category,
      description: service.description,
      basePrice: service.basePrice ?? '',
      deliverables: [...(service.deliverables || [])],
      features: [...(service.features || [])]
    });
    setShowAddModal(true);
  };

  const handleDeleteService = async (serviceId) => {
    const ok = await confirm('Are you sure you want to delete this service?');
    if (!ok) return;

    // Store original services for rollback
    const previousServices = services;
    
    // Optimistic UI update
    setServices((prev) => prev.filter((s) => s.id !== serviceId));
    
    // Attempt delete on server
    try {
      const token = session.getToken();
      if (!token) {
        // No token, restore services
        setServices(previousServices);
        return;
      }
      // Use _id if available, otherwise use id
      const deleteId = previousServices.find(s => s.id === serviceId)?._id || serviceId;
      const res = await fetch(`${API_BASE_URL}/services/${encodeURIComponent(deleteId)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        // Delete failed, restore services
        setServices(previousServices);
        console.error('Failed to delete service:', res.statusText);
      }
    } catch (e) {
      // Error during delete, restore services
      setServices(previousServices);
      console.error('Error deleting service:', e);
    }
  };

  const handleSubmit = async () => {
    // Validate required fields (basePrice is optional — empty defaults to 0)
    const errors = {};
    if (!formData.name || !formData.name.trim()) {
      errors.name = t('services.nameRequired');
    }
    if (!formData.category || !formData.category.trim()) {
      errors.category = t('services.categoryRequired');
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setFormErrors({});

    const normalizedBasePrice =
      formData.basePrice === '' || formData.basePrice == null
        ? 0
        : Number(formData.basePrice) || 0;

    if (editingService) {
      const previousServices = services;
      const updatedService = { ...editingService, name: formData.name, category: formData.category, description: formData.description, basePrice: normalizedBasePrice };
      
      // Update existing service (optimistic)
      setServices((prev) =>
        prev.map((s) =>
          s.id === editingService.id ? updatedService : s
        )
      );
      
      try {
        const token = session.getToken();
        if (!token) {
          setServices(previousServices);
          return;
        }
        // Use _id if available, otherwise use id
        const updateId = editingService._id || editingService.id;
        const res = await fetch(`${API_BASE_URL}/services/${encodeURIComponent(updateId)}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(updatedService),
        });
        if (!res.ok) {
          // Update failed, restore services
          setServices(previousServices);
          console.error('Failed to update service:', res.statusText);
          return;
        }
        const updated = await res.json();
        setServices((prev) => prev.map((s) => (s.id === editingService.id ? updated : s)));
        showToast('Service updated successfully', { type: 'success' });
      } catch (e) {
        // Error during update, restore services
        setServices(previousServices);
        console.error('Error updating service:', e);
      }
    } else {
      const newId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const newService = {
        id: newId,
        name: formData.name,
        category: formData.category,
        description: formData.description,
        basePrice: normalizedBasePrice,
        deliverables: formData.deliverables || [],
        features: formData.features || [],
      };
      // Optimistic add
      setServices((prev) => [...prev, newService]);
      
      try {
        const token = session.getToken();
        if (!token) {
          // No token, remove the optimistically added service
          setServices((prev) => prev.filter((s) => s.id !== newId));
          return;
        }
        const res = await fetch(`${API_BASE_URL}/services`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify(newService),
        });
        if (res.ok) {
          const created = await res.json();
          setServices((prev) => prev.map((s) => (s.id === newService.id ? created : s)));
          try { window.dispatchEvent(new CustomEvent('notifications:refresh')); setTimeout(()=>window.dispatchEvent(new CustomEvent('notifications:refresh')),600); setTimeout(()=>window.dispatchEvent(new CustomEvent('notifications:refresh')),1600);} catch(e){}
        } else {
          // Add failed, remove optimistic service
          setServices((prev) => prev.filter((s) => s.id !== newId));
          console.error('Failed to add service:', res.statusText);
        }
      } catch (e) {
        // Error during add, remove optimistic service
        setServices((prev) => prev.filter((s) => s.id !== newId));
        console.error('Error adding service:', e);
      }
    }
    setShowAddModal(false);
  };

  const location = useLocation();
  const pendingServiceIdRef = useRef(null);
  const [highlightedServiceId, setHighlightedServiceId] = useState(null);

  useEffect(() => {
    // Try to load services from server when authenticated. Fall back to local state.
    const load = async () => {
      try {
        const token = session.getToken();
        if (!token) return; // not authenticated
        const res = await fetch(`${API_BASE_URL}/services`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        if (Array.isArray(data)) setServices(data);
      } catch (e) {
        // ignore - keep local state
      }
    };
    load();

    if (location?.state?.openAdd) {
      handleAddService();
      try { window.history.replaceState({}, document.title); } catch (e) {}
    }
    // Deep-link for push clicks when app was closed (SW opens /services?id=...)
    const handleDeepLink = () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const qId = params.get('id');
        if (qId) {
          const svc = services.find(s => String(s.id) === String(qId) || String(s._id) === String(qId));
          if (svc) {
            setHighlightedServiceId(String(qId));
            handleEditService(svc);
            const url = new URL(window.location.href);
            url.searchParams.delete('id');
            window.history.replaceState({}, '', url.pathname + (url.search ? `?${url.searchParams.toString()}` : '') + url.hash);
          } else {
            pendingServiceIdRef.current = String(qId);
          }
        }
      } catch (e) {}
    };
    handleDeepLink();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Retry pending deep-link when services load
  useEffect(() => {
    if (pendingServiceIdRef.current && services.length) {
      const svc = services.find(s => String(s.id) === String(pendingServiceIdRef.current) || String(s._id) === String(pendingServiceIdRef.current));
      if (svc) {
        setHighlightedServiceId(pendingServiceIdRef.current);
        handleEditService(svc);
        pendingServiceIdRef.current = null;
        try {
          const url = new URL(window.location.href);
          url.searchParams.delete('id');
          window.history.replaceState({}, '', url.pathname + (url.search ? `?${url.searchParams.toString()}` : '') + url.hash);
        } catch (e) {}
      }
    }
  }, [services]);

  // Handle push deep-link when app already open (SW client.navigate)
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const qId = params.get('id');
      if (qId) {
        const svc = services.find(s => String(s.id) === String(qId) || String(s._id) === String(qId));
        if (svc) {
          setHighlightedServiceId(String(qId));
          handleEditService(svc);
          const url = new URL(window.location.href);
          url.searchParams.delete('id');
          window.history.replaceState({}, '', url.pathname + (url.search ? `?${url.searchParams.toString()}` : '') + url.hash);
        }
      }
    } catch (e) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  useEffect(() => {
    const handler = (ev) => {
      try {
        const detail = ev && ev.detail ? ev.detail : null;
        if (!detail || detail.path !== '/services') return;
        const state = detail.state || {};
        if (state.openViewId) {
          const svc = services.find(s => String(s.id) === String(state.openViewId) || String(s._id) === String(state.openViewId));
          if (svc) {
            setHighlightedServiceId(String(state.openViewId));
            handleEditService(svc);
          } else pendingServiceIdRef.current = String(state.openViewId);
        } else if (state.openAdd) handleAddService();
      } catch (e) {}
    };
    window.addEventListener('ui:notification', handler);
    return () => window.removeEventListener('ui:notification', handler);
  }, [services]);

  const updateFormArray = (field, index, value) => {
    // no-op: arrays removed from modal UI but keep function for compatibility
    const newArray = [...(formData[field] || [])];
    newArray[index] = value;
    setFormData({ ...formData, [field]: newArray });
  };

  const addFormArrayItem = (field) => {
    setFormData({ ...formData, [field]: [...(formData[field] || []), ''] });
  };

  const removeFormArrayItem = (field, index) => {
    setFormData({ 
      ...formData, 
      [field]: (formData[field] || []).filter((_, i) => i !== index) 
    });
  };

  const getFilteredServices = () => {
    const q = (searchQuery || '').toString().trim().toLowerCase();
    // Extract only digits and decimal point for a numeric comparison (e.g. "5000" or "₹5,000")
    const numericPart = (searchQuery || '').toString().replace(/[^0-9.]/g, '');
    const parsedNumber = numericPart === '' ? NaN : Number(numericPart);

    const results = services.filter(service => {
      const cat = service?.category || '';
      const name = (service?.name || '').toString().toLowerCase();
      const desc = (service?.description || '').toString().toLowerCase();
      const priceStr = service?.basePrice != null ? service.basePrice.toString() : '';

      const matchesCategory = filterCategory === 'all' || cat === filterCategory;

      // Textual match (name or description)
      const matchesText = q === '' || name.includes(q) || desc.includes(q);

      // Price match: if query contains digits, allow matching numeric value or substring of price
      const matchesPrice = (!isNaN(parsedNumber) && numericPart !== '')
        ? (Number(service.basePrice) === parsedNumber || priceStr.includes(parsedNumber.toString()))
        : false;

      // Combine text and price matches: if query is empty we pass through, otherwise either text or price match is enough
      const matchesSearch = q === '' ? true : (matchesText || matchesPrice);

      return matchesCategory && matchesSearch;
    });

    // Apply price sorting if requested
    if (priceSort === 'low') {
      return results.slice().sort((a, b) => (Number(a.basePrice) || 0) - (Number(b.basePrice) || 0));
    }
    if (priceSort === 'high') {
      return results.slice().sort((a, b) => (Number(b.basePrice) || 0) - (Number(a.basePrice) || 0));
    }

    return results;
  };

  const getCategoryIcon = (category) => {
    // Always use the concierge bell icon for consistency across cards
    return <FaConciergeBell />;
  };

  const filteredServices = getFilteredServices();

  // Pagination calculations
  const totalPages = Math.ceil(filteredServices.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedServices = filteredServices.slice(startIndex, endIndex);

  const handlePageChange = (newPage) => setCurrentPage(newPage);
  const handleRowsPerPageChange = (newRows) => { setRowsPerPage(newRows); setCurrentPage(1); };

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filterCategory, searchQuery, priceSort]);

  // Count unique categories (exclude the 'all' placeholder)
  const categoryCount = Math.max(0, getUniqueCategories().filter(c => c !== 'all').length);

  // persist whenever services change (sync to server only)
  useEffect(() => {
    try { storage.setJSONAndSync(STORAGE_KEYS.SERVICES, services); } catch (e) {}
  }, [services]);

  return (
    <div className="services">
      {/* Header */}
      <PageHero
        icon={<FaConciergeBell />}
        title={t('pages.services')}
        subtitle={t('services.subtitle')}
        actions={
          <button className="page-hero__btn" onClick={handleAddService}>
            <FaPlus />
            {t('services.addNew')}
          </button>
        }
      />

       {/* Stats Cards */}
      <div className="services__stats">
        <div className="services__stat-card">
          <div className="services__stat-icon services__stat-icon--indigo">
            <FaConciergeBell className="services__stat-svg--indigo" />
          </div>
          <div className="services__stat-info">
            <h3>{services.length}</h3>
            <p>{t('services.totalServices')}</p>
          </div>
        </div>

        <div className="services__stat-card">
          <div className="services__stat-icon services__stat-icon--indigo">
            <FaBriefcase className="services__stat-svg--indigo" />
          </div>
          <div className="services__stat-info">
            <h3>{categoryCount}</h3>
            <p>{t('services.totalCategories')}</p>
          </div>
        </div>
        
        <div className="services__stat-card">
          <div className="services__stat-icon services__stat-icon--sky">
            <FaRupeeSign className="services__stat-svg--sky" />
          </div>
          <div className="services__stat-info">
            <h3>{Number(services.reduce((sum, s) => sum + (Number(s.basePrice) || 0), 0)).toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })}</h3>
            <p>{t('services.totalValue')}</p>
          </div>
        </div>

        <div className="services__stat-card">
          <div className="services__stat-icon services__stat-icon--indigo">
            <FaCalculator className="services__stat-svg--indigo" />
          </div>
          <div className="services__stat-info">
            <h3>{services.length > 0 ? Number(services.reduce((sum, s) => sum + (Number(s.basePrice) || 0), 0) / services.length).toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }) : '₹0'}</h3>
            <p>{t('services.averagePrice')}</p>
          </div>
        </div>

      </div>


      {/* Toolbar */}
      <div className="services__toolbar">
        <div className="services__search">
          <FaSearch />
          <input
            type="text"
            placeholder="Search services..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <div className="services__filters">
          <div className="services__filter">
            <label>Price:</label>
            <CustomSelect
              value={priceSort}
              onChange={setPriceSort}
              options={[
                { value: 'default', label: 'Default' },
                { value: 'low', label: 'Price: Low to High' },
                { value: 'high', label: 'Price: High to Low' }
              ]}
            />
          </div>
          <div className="services__filter">
            <label>{t('services.categoryLabel')}</label>
            <CustomSelect
              value={filterCategory}
              onChange={setFilterCategory}
              options={getUniqueCategories().map(cat => ({
                value: cat,
                label: cat === 'all' ? t('services.allCategories') : cat
              }))}
            />
          </div>
        </div>
      </div>

      {/* Services Grid */}
      <div className="services__content">
        {filteredServices.length === 0 ? (
          <div className="services__empty">
            <FaConciergeBell />
            <h3>{t('services.noFound')}</h3>
            <p>{t('services.noFoundHint')}</p>
          </div>
        ) : (
          <div className="services__grid">
            {paginatedServices.map(service => {
              const isHighlighted = highlightedServiceId === String(service.id) || highlightedServiceId === String(service._id);
              return (
              <div key={service.id} data-service-id={service.id} className={`services__card ${isHighlighted ? 'services__card--highlight' : ''}`} style={isHighlighted ? { boxShadow: '0 0 0 3px #6366f1, 0 8px 24px rgba(99,102,241,0.3)', borderColor: '#6366f1' } : undefined}>
                
                <div className="services__card-header">
                  <div className="services__card-icon">
                    {getCategoryIcon(service.category)}
                  </div>
                  <div className="services__card-actions">
                    <button 
                      className="services__action-btn services__action-btn--edit"
                      onClick={() => handleEditService(service)}
                      title="Edit"
                    >
                      <FaEdit />
                    </button>
                    <button 
                      className="services__action-btn services__action-btn--delete"
                      onClick={() => handleDeleteService(service.id)}
                      title="Delete"
                    >
                      <FaTrash />
                    </button>
                  </div>
                </div>

                <div className="services__card-body">
                  <span className="services__category">{service.category}</span>
                  <h3 className="services__card-title">{service.name}</h3>
                  {service.description ? <p className="services__description">{service.description}</p> : null}

                 
                    {/* Only showing basic info per requirement */}
                  </div>

                  {/* deliverables and features intentionally hidden per requirement */}

                

                <div className="services__card-footer">
                  <div className="services__price">
                    <span className="services__price-label">{t('services.startingAt')}</span>
                    <span className="services__price-value">{(Number(service.basePrice) || 0).toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })}</span>
                  </div>
                  {/* status removed per request */}
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Pagination Controls */}
      {filteredServices.length > 0 && (
        <div className="services__pagination">
          <div className="services__pagination-info">
            {t('services.showing')} {startIndex + 1} {t('services.to')} {Math.min(endIndex, filteredServices.length)} {t('services.of')} {filteredServices.length} {t('services.servicesLabel')}
          </div>
          <div className="services__pagination-controls">
            <div className="services__pagination-rows">
              <label>{t('services.perPage')}</label>
              <CustomSelect
                value={rowsPerPage}
                onChange={handleRowsPerPageChange}
                options={[
                  { value: 9, label: '9' },
                  { value: 18, label: '18' },
                  { value: 27, label: '27' },
                ]}
              />
            </div>
            <div className="services__pagination-buttons">
              <button className="services__pagination-btn" onClick={() => handlePageChange(1)} disabled={currentPage === 1} title="First Page"><FaAngleDoubleLeft /></button>
              <button className="services__pagination-btn" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} title="Previous Page"><FaAngleLeft /></button>
              <div className="services__pagination-pages">
                {(() => {
                  const pages = [];
                  const maxVisible = 5;
                  let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
                  let endPage = Math.min(totalPages, startPage + maxVisible - 1);
                  if (endPage - startPage < maxVisible - 1) startPage = Math.max(1, endPage - maxVisible + 1);
                  if (startPage > 1) {
                    pages.push(<button key={1} className="services__pagination-btn" onClick={() => handlePageChange(1)}>1</button>);
                    if (startPage > 2) pages.push(<span key="e1" className="services__pagination-ellipsis">...</span>);
                  }
                  for (let i = startPage; i <= endPage; i++) {
                    pages.push(<button key={i} className={`services__pagination-btn${currentPage === i ? ' active' : ''}`} onClick={() => handlePageChange(i)}>{i}</button>);
                  }
                  if (endPage < totalPages) {
                    if (endPage < totalPages - 1) pages.push(<span key="e2" className="services__pagination-ellipsis">...</span>);
                    pages.push(<button key={totalPages} className="services__pagination-btn" onClick={() => handlePageChange(totalPages)}>{totalPages}</button>);
                  }
                  return pages;
                })()}
              </div>
              <button className="services__pagination-btn" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} title="Next Page"><FaAngleRight /></button>
              <button className="services__pagination-btn" onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} title="Last Page"><FaAngleDoubleRight /></button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Service Modal */}
      <Modal
        isOpen={showAddModal}
        title={editingService ? t('services.editTitle') : t('services.addTitle')}
        onClose={() => setShowAddModal(false)}
      >
        <div className="services__form">
            <div className="services__form-group">
              <label>{t('services.nameLabel')} <span className="required">*</span></label>
              <input
                type="text"
                placeholder="e.g., Wedding Photography"
                value={formData.name}
                onChange={(e) => {
                  setFormData({ ...formData, name: e.target.value });
                  if (formErrors.name) setFormErrors({ ...formErrors, name: '' });
                }}
                className={formErrors.name ? 'input-error' : ''}
              />
              {formErrors.name && <span className="error-message">{formErrors.name}</span>}
            </div>

            <div className="services__form-row">
              <div className="services__form-group">
                <label>{t('services.categoryFormLabel')} <span className="required">*</span></label>
                {!showNewCategoryInput ? (
                  <div className="services__category-selector">
                    <CustomSelect
                      value={formData.category || ''}
                      placeholder={t('services.selectCategory')}
                      className={formErrors.category ? 'input-error' : ''}
                      options={[
                        ...getUniqueCategories()
                          .filter(c => c !== 'all')
                          .map(cat => ({ value: cat, label: cat })),
                        { value: '__ADD_NEW__', label: `+ ${t('services.addNewCategory')}` }
                      ]}
                      onChange={(val) => {
                        if (val === '__ADD_NEW__') {
                          setShowNewCategoryInput(true);
                          setNewCategoryInput('');
                        } else {
                          setFormData({ ...formData, category: val });
                          if (formErrors.category) setFormErrors({ ...formErrors, category: '' });
                        }
                      }}
                    />
                    {formErrors.category && <span className="error-message">{formErrors.category}</span>}
                  </div>
                ) : (
                  <div className="services__category-input-wrapper">
                    <div className="services__category-input-group">
                      <input
                        type="text"
                        placeholder={t('services.enterCategoryName')}
                        value={newCategoryInput}
                        onChange={(e) => setNewCategoryInput(e.target.value)}
                        autoFocus
                        onKeyPress={(e) => {
                          if (e.key === 'Enter' && newCategoryInput.trim()) {
                            setFormData({ ...formData, category: newCategoryInput.trim() });
                            if (formErrors.category) setFormErrors({ ...formErrors, category: '' });
                            setShowNewCategoryInput(false);
                          }
                        }}
                      />
                      <button
                        type="button"
                        className="services__category-btn services__category-btn--confirm"
                        onClick={() => {
                          if (newCategoryInput.trim()) {
                            setFormData({ ...formData, category: newCategoryInput.trim() });
                            if (formErrors.category) setFormErrors({ ...formErrors, category: '' });
                            setShowNewCategoryInput(false);
                          }
                        }}
                        title="Confirm"
                      >
                        ✓
                      </button>
                      <button
                        type="button"
                        className="services__category-btn services__category-btn--cancel"
                        onClick={() => setShowNewCategoryInput(false)}
                        title="Cancel"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                )}
              </div>
              <div className="services__form-group">
                <label>{t('services.priceLabel')}</label>
                <input
                  type="number"
                  placeholder="0"
                  value={formData.basePrice}
                  min="0"
                  onChange={(e) => {
                    const raw = e.target.value;
                    setFormData({ ...formData, basePrice: raw === '' ? '' : Number(raw) });
                  }}
                />
              </div>
            </div>

            <div className="services__form-group">
              <label>{t('services.descLabel')}</label>
              <textarea
                placeholder="Brief description of the service (optional)"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
              />
            </div>
            {/* Only basic fields required: name, category (description optional, basePrice optional defaults to 0) */}

            {/* Popular/Active checkboxes removed per request */}

            <div className="services__form-actions">
              <button className="btn-secondary" onClick={() => setShowAddModal(false)}>
                {t('common.cancel')}
              </button>
              <button
                className="btn-primary"
                onClick={handleSubmit}
                disabled={!formData.name?.trim() || !formData.category?.trim()}
              >
                {editingService ? t('services.update') : t('services.save')}
              </button>
            </div>
        </div>
      </Modal>
    </div>
  );
};

export default Services;
